import React, { useEffect, useId, useMemo, useState } from 'react';
import { useOrgStore } from '@/stores/orgStore';
import { useOrgMasterDataUpdate } from '@/hooks/queries';
import { Card } from '@/components/ui/Card';
import { Input } from '@/components/ui/Input';
import { Select } from '@/components/ui/Select/Select';
import { Button } from '@/components/ui/Button';
import { showToast } from '@/components/ui/Toast';
import { SimpleTooltip } from '@/components/ui/Tooltip/Tooltip';
import { UnsavedChangesDialog } from '@/components/ui/UnsavedChangesDialog';
import { useUnsavedChangesGuard } from '@/hooks/useUnsavedChangesGuard';
import { t } from '@/lib/i18n';
import { getCountryOptions } from '@/lib/countries';
import { Building2, MapPin, Info } from 'lucide-react';
import { useOverarchingEditable } from './OverarchingEditableContext';

/**
 * CompanyDataPanel — Company Profile → Data tab.
 *
 * Editable organization master data: name, website, contact email, and
 * address fields. `slug` is displayed read-only as it's the URL identity
 * for the org and changing it would break every existing link. The backend
 * PATCH /api/orgs/:id enforces that server-side too.
 *
 * Local edit state seeded from the Zustand org store on mount; `Save` calls
 * useOrgMasterDataUpdate which refreshes the store on success.
 *
 * Navigation blocking: `useUnsavedChangesGuard` arms a router-level blocker
 * whenever `isDirty` is true. Any in-app navigation shows the 3-way
 * Save / Discard / Cancel dialog before proceeding.
 */

interface EditableFields {
  name: string;
  website_url: string;
  contact_email: string;
  address_street: string;
  address_city: string;
  address_zip: string;
  address_country: string;
}

const EMPTY: EditableFields = {
  name: '',
  website_url: '',
  contact_email: '',
  address_street: '',
  address_city: '',
  address_zip: '',
  address_country: '',
};

export function CompanyDataPanel() {
  const activeOrgId = useOrgStore((s) => s.activeOrgId);
  const activeOrg = useOrgStore((s) => s.orgs.find((o) => o.id === activeOrgId));
  const updateMasterData = useOrgMasterDataUpdate();
  const { canEdit } = useOverarchingEditable();

  // SSR-safe stable IDs for label ↔ input associations (MED-7 a11y fix)
  const slugInputId = useId();
  const countrySelectId = useId();

  const serverValue = useMemo<EditableFields>(() => {
    if (!activeOrg) return EMPTY;
    return {
      name: activeOrg.name ?? '',
      website_url: activeOrg.website_url ?? '',
      contact_email: activeOrg.contact_email ?? '',
      address_street: activeOrg.address_street ?? '',
      address_city: activeOrg.address_city ?? '',
      address_zip: activeOrg.address_zip ?? '',
      address_country: activeOrg.address_country ?? '',
    };
  }, [activeOrg]);

  const [edited, setEdited] = useState<EditableFields>(serverValue);
  useEffect(() => {
    setEdited(serverValue);
  }, [serverValue]);

  const isDirty = useMemo(() => {
    return (Object.keys(edited) as Array<keyof EditableFields>).some(
      (k) => edited[k] !== serverValue[k],
    );
  }, [edited, serverValue]);

  const handleChange = (key: keyof EditableFields) => (e: React.ChangeEvent<HTMLInputElement>) => {
    setEdited((prev) => ({ ...prev, [key]: e.target.value }));
  };

  const handleDiscard = () => setEdited(serverValue);

  const handleSave = async () => {
    if (!activeOrgId || !isDirty) return;

    // Build diff payload — only send changed fields, normalise empty strings
    // to null so the backend can clear optional fields cleanly.
    const payload: Record<string, string | null | undefined> = {};
    (Object.keys(edited) as Array<keyof EditableFields>).forEach((key) => {
      if (edited[key] === serverValue[key]) return;
      if (key === 'name') {
        payload[key] = edited[key];
      } else {
        payload[key] = edited[key] === '' ? null : edited[key];
      }
    });

    await updateMasterData.mutateAsync({ orgId: activeOrgId, payload });
    showToast.success(t('Organisation data saved.'));
  };

  // B15 / M6-arch: Block in-app navigation when form is dirty.
  // The guard arms a RR6 useBlocker and a beforeunload listener so no edits
  // are silently lost on tab switch or sidebar navigation.
  const unsavedGuard = useUnsavedChangesGuard({
    isDirty,
    onSave: handleSave,
    onDiscard: handleDiscard,
  });

  if (!activeOrg) {
    return (
      <Card padding="md">
        <p className="text-muted-foreground">{t('No active organisation.')}</p>
      </Card>
    );
  }

  return (
    <>
      <div className="space-y-6" data-testid="company-data-panel">
        {/* Organisation identity */}
        <Card padding="md">
          <div className="flex items-center gap-2 mb-4">
            <Building2 className="h-5 w-5 text-primary-600" />
            <h3 className="font-semibold">{t('Organisation')}</h3>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Input
              label={t('Name')}
              value={edited.name}
              onChange={handleChange('name')}
              data-testid="company-data-name"
              readOnly={!canEdit}
            />
            {/* MED-7 a11y: stable htmlFor/id pair for the read-only slug field */}
            <div>
              <div className="flex items-center gap-1 mb-1">
                <label
                  htmlFor={slugInputId}
                  className="block text-sm font-medium text-foreground"
                >
                  {t('Slug')}
                </label>
                <SimpleTooltip
                  content={t(
                    'The slug cannot be changed — it is used in URLs and references throughout the system.',
                  )}
                >
                  <Info className="h-3.5 w-3.5 text-muted-foreground" />
                </SimpleTooltip>
              </div>
              <Input
                id={slugInputId}
                value={activeOrg.slug}
                disabled
                data-testid="company-data-slug"
                data-no-translate
              />
            </div>
            <Input
              label={t('Website')}
              type="url"
              value={edited.website_url}
              onChange={handleChange('website_url')}
              placeholder="https://example.com"
              data-testid="company-data-website"
              readOnly={!canEdit}
            />
            <Input
              label={t('Contact Email')}
              type="email"
              value={edited.contact_email}
              onChange={handleChange('contact_email')}
              placeholder="contact@example.com"
              data-testid="company-data-contact-email"
              readOnly={!canEdit}
            />
          </div>
        </Card>

        {/* Address */}
        <Card padding="md">
          <div className="flex items-center gap-2 mb-4">
            <MapPin className="h-5 w-5 text-primary-600" />
            <h3 className="font-semibold">{t('Address')}</h3>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="md:col-span-2">
              <Input
                label={t('Street')}
                value={edited.address_street}
                onChange={handleChange('address_street')}
                data-testid="company-data-street"
                readOnly={!canEdit}
              />
            </div>
            <Input
              label={t('ZIP / Postal Code')}
              value={edited.address_zip}
              onChange={handleChange('address_zip')}
              data-testid="company-data-zip"
              readOnly={!canEdit}
            />
            <Input
              label={t('City')}
              value={edited.address_city}
              onChange={handleChange('address_city')}
              data-testid="company-data-city"
              readOnly={!canEdit}
            />
            {/* MED-7 a11y: pass stable id so Select's internal label gets a
                deterministic htmlFor (prevents SSR hydration mismatch from
                Math.random()-based ids) */}
            <div className="md:col-span-2">
              <Select
                id={countrySelectId}
                label={t('Country')}
                value={edited.address_country}
                onChange={(e) =>
                  setEdited((prev) => ({ ...prev, address_country: e.target.value }))
                }
                data-testid="company-data-country"
                disabled={!canEdit}
              >
                {getCountryOptions().map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </Select>
            </div>
          </div>
        </Card>

        {/* Save / Discard — hidden in read-only mode */}
        {canEdit && (
          <div className="flex items-center justify-end gap-2">
            <Button
              variant="outline"
              onClick={handleDiscard}
              disabled={!isDirty || unsavedGuard.isSaving}
              data-testid="company-data-discard"
            >
              {t('Discard')}
            </Button>
            <Button
              onClick={() => void handleSave()}
              disabled={!isDirty || unsavedGuard.isSaving}
              isLoading={unsavedGuard.isSaving}
              data-testid="company-data-save"
            >
              {t('Save changes')}
            </Button>
          </div>
        )}
      </div>

      {/* Navigation guard dialog — shown when user tries to navigate away while dirty */}
      <UnsavedChangesDialog
        isOpen={unsavedGuard.isBlocked}
        onSave={() => void unsavedGuard.save()}
        onDiscard={unsavedGuard.discard}
        onCancel={unsavedGuard.cancel}
        isSaving={unsavedGuard.isSaving}
      />
    </>
  );
}
